import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MultiSelectFilter, { DETECTION_STATUS_PRESETS, SEVERITY_PRESETS } from '../components/common/MultiSelectFilter';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  AutoAwesome as CorrelateIcon,
  AutoAwesome as AIIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  Edit as EditIcon,
  FolderOpen as CaseIcon,
  CloudDownload as IngestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import DetectionBulkActions from '../components/DetectionBulkActions';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import { useTableSort } from '../hooks/useTableSort';
import usePersistedFilters from '../hooks/usePersistedFilters';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import { formatDateTime } from '../utils/dateUtils';

const EMPTY_OPTIONS = { statuses: [], severities: [], categories: [], sources: [] };
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const DETECTIONS_FILTER_DEFAULTS = {
  search: '', status: [], severity: [], category: '', assignedTo: '', sort: '-detectionTime', rowsPerPage: 20,
};

export const Detections = () => {
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor } = useSettings();
  const { hasPermission, canManageAgents } = usePermission();
  const navigate = useNavigate();

  const { filters, setFilter, reset: resetFilters } = usePersistedFilters('detections', DETECTIONS_FILTER_DEFAULTS);

  // List state (server-side filtering + pagination)
  const [detections, setDetections] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0); // zero-based for TablePagination, not persisted
  const rowsPerPage = filters.rowsPerPage;
  const setRowsPerPage = (value) => setFilter('rowsPerPage', value);
  const searchTerm = filters.search;
  const setSearchTerm = (value) => setFilter('search', value);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm.trim());
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);
  const statusFilter = filters.status; // multi-select
  const setStatusFilter = (value) => setFilter('status', value);
  const severityFilter = filters.severity;
  const setSeverityFilter = (value) => setFilter('severity', value);
  const categoryFilter = filters.category;
  const setCategoryFilter = (value) => setFilter('category', value);
  const assignedToFilter = filters.assignedTo;
  const setAssignedToFilter = (value) => setFilter('assignedTo', value);
  const { sort, field: sortField, direction: sortDirection, toggle: toggleSort, setSort } = useTableSort(filters.sort);

  // Keep the sort control and the persisted filters in sync in both
  // directions: adopt filters.sort when it changes externally (profile
  // hydration, "Clear filters"), otherwise persist local sort changes
  // made via the sort dropdown or column headers.
  const lastSyncedSort = useRef(filters.sort);
  useEffect(() => {
    if (sort === filters.sort) {
      lastSyncedSort.current = sort;
      return;
    }
    if (filters.sort !== lastSyncedSort.current) {
      lastSyncedSort.current = filters.sort;
      setSort(filters.sort);
    } else {
      lastSyncedSort.current = sort;
      setFilter('sort', sort);
    }
  }, [sort, filters.sort, setFilter, setSort]);

  // Reference data
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [users, setUsers] = useState([]);

  // Bulk selection state
  const [selectedDetections, setSelectedDetections] = useState([]);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [bulkInitialAction, setBulkInitialAction] = useState('');

  // Create detection dialog
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [alertData, setAlertData] = useState({ alertData: '', source: 'Manual' });
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Ingestion dialog
  const [ingestionModalOpen, setIngestionModalOpen] = useState(false);
  const [ingestion, setIngestion] = useState({ loading: false, results: null, error: null });

  // Correlation dialog
  const [correlationModalOpen, setCorrelationModalOpen] = useState(false);
  const [correlationStatus, setCorrelationStatus] = useState({ loading: false, success: false, error: null, results: null });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', action: null });
  const notify = (message, severity = 'success', action = null) => setSnackbar({ open: true, message, severity, action });

  const severityStyle = (severity) => {
    const color = getSeverityColor(severity) || {};
    return { backgroundColor: color.main || '#757575', color: color.contrastText || '#fff' };
  };
  const statusColor = (status) => {
    const color = getStatusColor(status);
    return (typeof color === 'string' ? color : color?.main) || '#757575';
  };

  // ---------- data loading ----------
  const fetchDetections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: page + 1, limit: rowsPerPage, sort };
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (severityFilter.length) params.severity = severityFilter.join(',');
      if (categoryFilter) params.category = categoryFilter;
      if (assignedToFilter) params.assignedTo = assignedToFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await apiService.detections.getAll(params);
      setDetections(response.data.detections || []);
      setPagination(response.data.pagination || { page: page + 1, limit: rowsPerPage, total: 0, pages: 0 });
    } catch (err) {
      console.error('Error fetching detections:', err);
      setError(err.response?.data?.message || 'Failed to load detections. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sort, statusFilter, severityFilter, categoryFilter, assignedToFilter, debouncedSearch]);

  useEffect(() => {
    fetchDetections();
  }, [fetchDetections]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, severityFilter, categoryFilter, assignedToFilter, rowsPerPage, sort]);

  useEffect(() => {
    apiService.detections.getOptions()
      .then((res) => setOptions({ ...EMPTY_OPTIONS, ...res.data }))
      .catch((err) => console.error('Error loading detection options:', err));

    apiService.users.getAll({ limit: 100 })
      .then((res) => setUsers(res.data.users || []))
      .catch((err) => console.error('Error loading users:', err));
  }, []);

  // ---------- selection ----------
  const idOf = (d) => d._id || d.id;
  const isDetectionSelected = (detection) => selectedDetections.some((d) => idOf(d) === idOf(detection));
  const selectedOnPage = useMemo(
    () => detections.filter((d) => selectedDetections.some((s) => idOf(s) === idOf(d))).length,
    [detections, selectedDetections]
  );
  const allOnPageSelected = detections.length > 0 && selectedOnPage === detections.length;

  const handleSelectDetection = (detection) => {
    setSelectedDetections((prev) => (
      prev.some((d) => idOf(d) === idOf(detection))
        ? prev.filter((d) => idOf(d) !== idOf(detection))
        : [...prev, detection]
    ));
  };

  const handleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedDetections((prev) => prev.filter((s) => !detections.some((d) => idOf(d) === idOf(s))));
    } else {
      setSelectedDetections((prev) => [...prev, ...detections.filter((d) => !prev.some((s) => idOf(s) === idOf(d)))]);
    }
  };

  const openBulkActions = (initialAction = '') => {
    setBulkInitialAction(initialAction);
    setBulkActionsOpen(true);
  };

  const handleBulkUpdate = () => {
    fetchDetections();
    setSelectedDetections([]);
  };

  const handleCreateCase = (newCase) => {
    setBulkActionsOpen(false);
    setSelectedDetections([]);
    fetchDetections();
    if (newCase?._id) {
      notify(`Case ${newCase.caseNumber || ''} created`, 'success', (
        <Button color="inherit" size="small" onClick={() => navigate(`/dashboard/cases/${newCase._id}`)}>View case</Button>
      ));
    }
  };

  // ---------- create detection ----------
  const handleCloseCreateModal = () => {
    if (submitting) return;
    setOpenCreateModal(false);
    setCreateError(null);
    setAlertData({ alertData: '', source: 'Manual' });
  };

  const handleCreateDetection = async () => {
    try {
      setSubmitting(true);
      setCreateError(null);
      await apiService.detections.processAlert(alertData);
      setOpenCreateModal(false);
      setAlertData({ alertData: '', source: 'Manual' });
      notify('Detection created');
      fetchDetections();
    } catch (err) {
      console.error('Error creating detection:', err);
      setCreateError(err.response?.data?.message || err.message || 'Failed to process alert data. Please check the format and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- ingestion ----------
  const handleDetectionIngestion = async () => {
    setIngestionModalOpen(true);
    setIngestion({ loading: true, results: null, error: null });
    try {
      const response = await apiService.agents.ingestAll();
      setIngestion({ loading: false, results: response.data, error: null });
      if (response.data?.summary?.totalDetections > 0) fetchDetections();
    } catch (err) {
      console.error('Error running detection ingestion:', err);
      setIngestion({ loading: false, results: null, error: err.response?.data?.message || err.message || 'Failed to run ingestion' });
    }
  };

  const handleCloseIngestionModal = () => {
    if (ingestion.loading) return;
    setIngestionModalOpen(false);
    setIngestion({ loading: false, results: null, error: null });
  };

  // ---------- correlation ----------
  const handleCaseCorrelation = async () => {
    setCorrelationModalOpen(true);
    setCorrelationStatus({ loading: true, success: false, error: null, results: null });
    try {
      const response = await apiService.ai.correlateCases(true);
      setCorrelationStatus({ loading: false, success: true, error: null, results: response.data });
      if (response.data.casesCreated > 0) fetchDetections();
    } catch (err) {
      console.error('Error correlating cases:', err);
      setCorrelationStatus({
        loading: false,
        success: false,
        error: err.response?.data?.message || err.message || 'Failed to analyze case correlations',
        results: null
      });
    }
  };

  const handleCloseCorrelationModal = () => {
    setCorrelationModalOpen(false);
    setCorrelationStatus({ loading: false, success: false, error: null, results: null });
  };

  const formatDetectionDateTime = (dateString) => formatDateTime(dateString, user?.preferences);
  const hasFilters = !!(debouncedSearch || statusFilter.length || severityFilter.length || categoryFilter || assignedToFilter);

  const clearFilters = () => {
    resetFilters();
    setDebouncedSearch('');
  };

  const ingestionSummary = ingestion.results?.summary;
  const ingestionResults = ingestion.results?.results || [];

  const handleExportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ status: statusFilter.join(','), severity: severityFilter.join(','), category: categoryFilter, assignedTo: assignedToFilter }).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    try {
      await apiService.download(`/detections/export${qs ? `?${qs}` : ''}`, `detections-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Detections</Typography>
          <Typography variant="body1" color="textSecondary">Manage and track security detections</Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          {hasPermission('cases', 'create') && (
            <Button variant="outlined" startIcon={<CorrelateIcon />} onClick={handleCaseCorrelation} disabled={correlationStatus.loading}>
              AI Case Correlation
            </Button>
          )}
          {canManageAgents && (
            <Button
              variant="outlined"
              startIcon={<IngestIcon />}
              onClick={handleDetectionIngestion}
              disabled={ingestion.loading}
              color="secondary"
            >
              {ingestion.loading ? 'Getting...' : 'Get Detections'}
            </Button>
          )}
          {hasPermission('detections', 'create') && (
            <Button variant="contained" startIcon={<AIIcon />} onClick={() => setOpenCreateModal(true)}>
              Create Detection
            </Button>
          )}
        </Box>
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedDetections.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="h6" color="primary">
                {selectedDetections.length} detection(s) selected
              </Typography>
              <Box display="flex" gap={1}>
                {hasPermission('detections', 'update') && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={() => openBulkActions('bulk-update')} size="small">
                    Bulk Actions
                  </Button>
                )}
                {hasPermission('cases', 'create') && (
                  <Button variant="outlined" startIcon={<CaseIcon />} onClick={() => openBulkActions('create-case')} size="small">
                    Create Case
                  </Button>
                )}
                <Button onClick={() => setSelectedDetections([])} size="small">Clear Selection</Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MultiSelectFilter label="Status" value={statusFilter} onChange={(v) => { setPage(0); setStatusFilter(v); }} options={options.statuses} presets={DETECTION_STATUS_PRESETS} allLabel="All statuses" />
            </Grid>
            <Grid item xs={6} md={2}>
              <MultiSelectFilter label="Severity" value={severityFilter} onChange={(v) => { setPage(0); setSeverityFilter(v); }} options={options.severities} presets={SEVERITY_PRESETS} allLabel="All severities" />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                  <MenuItem value="">All Categories</MenuItem>
                  {options.categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select value={assignedToFilter} label="Assigned To" onChange={(e) => setAssignedToFilter(e.target.value)}>
                  <MenuItem value="">Anyone</MenuItem>
                  {user?._id && <MenuItem value={user._id}>Me</MenuItem>}
                  {users.filter((u) => u._id !== user?._id).map((u) => (
                    <MenuItem key={u._id} value={u._id}>{u.firstName} {u.lastName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort</InputLabel>
                <Select value={sort} label="Sort" onChange={(e) => setSort(e.target.value)}
                  renderValue={(v) => ({ '-detectionTime': 'Detection time (newest)', '-createdAt': 'Created (newest)' }[v]) || `Column: ${v.replace(/^-/, '').replace(/\..*$/, '').replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())} (${v.startsWith('-') ? 'desc' : 'asc'})`}>
                  <MenuItem value="-detectionTime">Detection time (newest)</MenuItem>
                  <MenuItem value="-createdAt">Created (newest)</MenuItem>
                  {!['-detectionTime', '-createdAt'].includes(sort) && <MenuItem value={sort}>Column sort (click a header to change)</MenuItem>}
                </Select>
              </FormControl>
              {hasFilters && <Button size="small" onClick={clearFilters}>Clear filters</Button>}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {pagination.total} result{pagination.total === 1 ? '' : 's'}
              </Typography>
              <Tooltip title="Refresh">
                <span>
                  <IconButton size="small" onClick={fetchDetections} disabled={loading}><RefreshIcon /></IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchDetections}>Retry</Button>}>
              {error}
            </Alert>
          )}

          {loading && <LinearProgress sx={{ mb: 1 }} />}

          {/* Detections Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <IconButton
                      onClick={handleSelectAll}
                      size="small"
                      color={selectedOnPage > 0 ? 'primary' : 'default'}
                      disabled={detections.length === 0}
                      title={allOnPageSelected ? 'Deselect page' : 'Select page'}
                    >
                      {allOnPageSelected ? <CheckBoxIcon /> : selectedOnPage > 0 ? <IndeterminateCheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                    </IconButton>
                  </TableCell>
                  <SortableHeaderCell field="title" label="Title" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="severity" label="Severity" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="status" label="Status" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="category" label="Category" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="source" label="Source" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="detectionTime" label="Detection Time" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="assignedTo.lastName" label="Assigned To" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="case.caseNumber" label="Case" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      {loading ? (
                        <CircularProgress size={28} />
                      ) : (
                        <Typography variant="body1" color="textSecondary">
                          {hasFilters
                            ? 'No detections match your current filters.'
                            : 'No detections found. Create your first detection or connect an EDR integration to get started.'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  detections.map((detection) => (
                    <TableRow
                      key={idOf(detection)}
                      hover
                      selected={isDetectionSelected(detection)}
                      sx={{ opacity: detection.suppression?.isSuppressed ? 0.6 : 1 }}
                    >
                      <TableCell padding="checkbox">
                        <IconButton
                          onClick={() => handleSelectDetection(detection)}
                          size="small"
                          color={isDetectionSelected(detection) ? 'primary' : 'default'}
                        >
                          {isDetectionSelected(detection) ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/dashboard/detections/${idOf(detection)}`}
                          underline="hover"
                          color="inherit"
                          variant="body2"
                          fontWeight="medium"
                        >
                          {detection.title}
                        </Link>
                        {detection.suppression?.isSuppressed && (
                          <Chip label="Suppressed" size="small" variant="outlined" sx={{ ml: 1 }} />
                        )}
                        {detection.aiAnalysis?.status === 'pending' && (
                          <Tooltip title={detection.aiAnalysis?.pendingMessage || 'Waiting for AI capacity.'}>
                            <Chip label="AI queued" size="small" variant="outlined" color="warning" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={detection.severity} size="small" sx={severityStyle(detection.severity)} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={detection.status}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: statusColor(detection.status), color: statusColor(detection.status) }}
                        />
                      </TableCell>
                      <TableCell>{detection.category || '-'}</TableCell>
                      <TableCell>
                        {detection.source || '-'}
                        {detection.vendor?.agentName && (
                          <Typography variant="caption" color="text.secondary" display="block">{detection.vendor.agentName}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDetectionDateTime(detection.detectionTime || detection.createdAt)}</TableCell>
                      <TableCell>
                        {detection.assignedTo ? `${detection.assignedTo.firstName} ${detection.assignedTo.lastName}` : 'Unassigned'}
                      </TableCell>
                      <TableCell>
                        {detection.case ? (
                          <Link component={RouterLink} to={`/dashboard/cases/${detection.case._id}`} underline="hover" variant="body2">
                            {detection.case.caseNumber || 'Case'}
                          </Link>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => navigate(`/dashboard/detections/${idOf(detection)}`)} title="View detection details">
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </CardContent>
      </Card>

      {/* Create Detection Modal */}
      <Dialog open={openCreateModal} onClose={handleCloseCreateModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="primary" />
            Create Detection with AI
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Paste your raw security alert data in any format (JSON, CSV, plain text, logs, etc.).
            Our AI will automatically extract all relevant information and create a structured detection.
          </Alert>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
              {createError}
              {/limit/i.test(createError) && (
                <>
                  {' '}
                  <Link component={RouterLink} to="/dashboard/billing">Upgrade your plan</Link>
                </>
              )}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Raw Alert Data"
                value={alertData.alertData}
                onChange={(e) => setAlertData((prev) => ({ ...prev, alertData: e.target.value }))}
                multiline
                rows={12}
                required
                disabled={submitting}
                inputProps={{ maxLength: 50000 }}
                placeholder={`Paste your alert data here. Examples:

JSON Format:
{"alert_id": "12345", "severity": "high", "source_ip": "192.168.1.100", "description": "Suspicious activity detected"}

CSV Format:
timestamp,source_ip,destination_ip,alert_type,severity
2024-01-15 14:30:00,192.168.1.100,10.0.0.5,malware_detected,critical

Plain Text:
Failed login attempts detected from IP 192.168.1.100
User: admin
Attempts: 50
Time: 2024-01-15 14:30:00

Log Format:
[2024-01-15 14:30:00] ALERT: Suspicious PowerShell execution detected on WORKSTATION-01
Process: powershell.exe -enc <base64_encoded_command>
Parent: winword.exe
User: john.doe`}
                helperText="AI will analyze this data and automatically populate all detection fields"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel>Alert Source</InputLabel>
                <Select
                  value={alertData.source}
                  label="Alert Source"
                  onChange={(e) => setAlertData((prev) => ({ ...prev, source: e.target.value }))}
                >
                  {(options.sources.length ? options.sources : ['Manual']).map((s) => (
                    <MenuItem key={s} value={s}>{s === 'Manual' ? 'Manual Entry' : s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleCreateDetection}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
            disabled={!alertData.alertData.trim() || submitting}
          >
            {submitting ? 'Analyzing...' : 'Create Detection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <DetectionBulkActions
        open={bulkActionsOpen}
        initialAction={bulkInitialAction}
        selectedDetections={selectedDetections}
        onBulkUpdate={handleBulkUpdate}
        onCreateCase={handleCreateCase}
        onClose={() => setBulkActionsOpen(false)}
        users={users}
        options={options}
      />

      {/* AI Case Correlation Status Modal */}
      <Dialog
        open={correlationModalOpen}
        onClose={correlationStatus.loading ? undefined : handleCloseCorrelationModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {correlationStatus.loading && <CircularProgress size={24} />}
          {correlationStatus.success && <SuccessIcon color="success" />}
          {correlationStatus.error && <ErrorIcon color="error" />}
          AI Case Correlation
          {!correlationStatus.loading && (
            <IconButton onClick={handleCloseCorrelationModal} sx={{ ml: 'auto' }} size="small"><CloseIcon /></IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {correlationStatus.loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>Analyzing Detections...</Typography>
              <Typography variant="body2" color="text.secondary">
                AI is analyzing open detections for correlation patterns and creating cases automatically.
              </Typography>
            </Box>
          )}

          {correlationStatus.success && correlationStatus.results && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle1">{correlationStatus.results.message || 'AI case correlation completed'}</Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="primary" gutterBottom>{correlationStatus.results.detectionsAnalyzed || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Detections Analyzed</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="warning.main" gutterBottom>{correlationStatus.results.caseSuggestions?.length || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Correlations Found</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main" gutterBottom>{correlationStatus.results.casesCreated || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Cases Created</Typography>
                  </Card>
                </Grid>
              </Grid>

              {correlationStatus.results.caseSuggestions?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>Correlated Cases</Typography>
                  {correlationStatus.results.caseSuggestions.map((suggestion, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {index + 1}. {suggestion.title}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Chip label={`${suggestion.severity} Severity`} size="small" sx={{ mr: 1, ...severityStyle(suggestion.severity) }} />
                        <Chip label={`${suggestion.confidence}% Confidence`} color="success" size="small" sx={{ mr: 1 }} />
                        {suggestion.correlationType && <Chip label={`${suggestion.correlationType} Correlation`} color="primary" size="small" />}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{suggestion.description}</Typography>
                      <Typography variant="subtitle2" gutterBottom>Detections ({suggestion.detectionCount}):</Typography>
                      <Box sx={{ pl: 2 }}>
                        {suggestion.detections?.map((detection, detIndex) => (
                          <Box key={detection.id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 20 }}>{detIndex + 1}.</Typography>
                            <Chip label={detection.severity} size="small" sx={{ minWidth: 60, ...severityStyle(detection.severity) }} />
                            <Typography variant="body2" sx={{ flex: 1 }}>{detection.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDetectionDateTime(detection.createdAt)}</Typography>
                          </Box>
                        ))}
                      </Box>
                      {suggestion.reasoning && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                          AI reasoning: {suggestion.reasoning}
                        </Typography>
                      )}
                    </Card>
                  ))}
                  {correlationStatus.results.casesCreated > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Linked detections have been closed and assigned to their cases.{' '}
                      <Link component={RouterLink} to="/dashboard/cases">Go to Cases</Link>
                    </Typography>
                  )}
                </Box>
              )}

              {(!correlationStatus.results.caseSuggestions || correlationStatus.results.caseSuggestions.length === 0) && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    No correlation patterns were found in the current open detections. All detections may be isolated
                    incidents, already assigned to cases, or there may not be enough open detections to identify patterns.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {correlationStatus.error && (
            <Alert severity="error">
              <Typography variant="subtitle1" gutterBottom>Correlation analysis failed</Typography>
              <Typography variant="body2">{correlationStatus.error}</Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!correlationStatus.loading && (
            <Button onClick={handleCloseCorrelationModal} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Detection Ingestion Modal */}
      <Dialog open={ingestionModalOpen} onClose={ingestion.loading ? undefined : handleCloseIngestionModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {ingestion.loading && <CircularProgress size={24} />}
          {ingestion.results && !ingestion.error && <SuccessIcon color="success" />}
          {ingestion.error && <ErrorIcon color="error" />}
          Get Detections from Integrations
          {!ingestion.loading && (
            <IconButton onClick={handleCloseIngestionModal} sx={{ ml: 'auto' }} size="small"><CloseIcon /></IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {ingestion.loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>Pulling alerts from your integrations...</Typography>
              <Typography variant="body2" color="text.secondary">
                Each enabled EDR integration is queried for new alerts. New alerts are de-duplicated, enriched and
                turned into detections. This can take a minute.
              </Typography>
            </Box>
          )}

          {ingestion.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Detection ingestion failed</Typography>
              <Typography variant="body2">{ingestion.error}</Typography>
            </Alert>
          )}

          {ingestion.results && ingestionSummary && ingestionSummary.totalAgents === 0 && (
            <Alert
              severity="info"
              icon={<LinkIcon />}
              action={<Button color="inherit" size="small" onClick={() => navigate('/dashboard/integrations')}>Set up integrations</Button>}
            >
              <Typography variant="subtitle1" gutterBottom>No EDR integrations configured</Typography>
              <Typography variant="body2">
                Connect CrowdStrike, SentinelOne or Microsoft Defender on the{' '}
                <Link component={RouterLink} to="/dashboard/integrations">Integrations</Link> page to pull alerts automatically.
              </Typography>
            </Alert>
          )}

          {ingestion.results && ingestionSummary && ingestionSummary.totalAgents > 0 && (
            <Box>
              <Alert severity={ingestionSummary.failedAgents > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
                <Typography variant="body2">{ingestion.results.message}</Typography>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="primary" gutterBottom>{ingestionSummary.totalAgents}</Typography>
                    <Typography variant="body2" color="text.secondary">Integrations</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main" gutterBottom>{ingestionSummary.successfulAgents}</Typography>
                    <Typography variant="body2" color="text.secondary">Successful</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="error.main" gutterBottom>{ingestionSummary.failedAgents}</Typography>
                    <Typography variant="body2" color="text.secondary">Failed</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="info.main" gutterBottom>{ingestionSummary.totalDetections}</Typography>
                    <Typography variant="body2" color="text.secondary">New Detections</Typography>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Per-integration results</Typography>
              {ingestionResults.map((result) => (
                <Card key={result.agentId} sx={{ mb: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {result.skipped ? (
                      <Chip label="Skipped" size="small" />
                    ) : result.success ? (
                      <SuccessIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">{result.agentName}</Typography>
                      <Typography variant="caption" color="text.secondary">{result.type}</Typography>
                    </Box>
                    {result.skipped ? (
                      <Typography variant="body2" color="text.secondary">{result.reason || 'Ingestion disabled'}</Typography>
                    ) : result.success ? (
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label={`${result.alertsProcessed || 0} alerts`} size="small" variant="outlined" />
                        <Chip label={`${result.detectionsCreated || 0} created`} size="small" color="success" />
                        {result.detectionsUpdated > 0 && <Chip label={`${result.detectionsUpdated} updated`} size="small" color="info" />}
                        {result.skipped > 0 && <Chip label={`${result.skipped} already ingested`} size="small" variant="outlined" />}
                        {result.errors?.length > 0 && <Chip label={`${result.errors.length} errors`} size="small" color="warning" />}
                      </Box>
                    ) : (
                      <Chip label="Failed" color="error" size="small" />
                    )}
                  </Box>
                  {!result.success && result.error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1, ml: 5 }}>{result.error}</Typography>
                  )}
                  {result.success && result.errors?.length > 0 && (
                    <Box sx={{ mt: 1, ml: 5 }}>
                      {result.errors.slice(0, 5).map((e, i) => (
                        <Typography key={i} variant="caption" color="warning.main" display="block">
                          {e.stage ? `[${e.stage}] ` : ''}{e.sourceId ? `${e.sourceId}: ` : ''}{e.error}
                        </Typography>
                      ))}
                      {result.errors.length > 5 && (
                        <Typography variant="caption" color="text.secondary">...and {result.errors.length - 5} more</Typography>
                      )}
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!ingestion.loading && (
            <Button onClick={handleCloseIngestionModal} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          action={snackbar.action}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Detections;
